import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';

@customElement('luna-control-choices')
export class LunaControlChoices extends LitPage {
  @property({ attribute: 'label' }) label = '';
  @property({ attribute: 'hint' }) hint = '';
  @property({ type: Array }) choices: string[] = [];
  @property({ type: Number }) value = 0;

  private select(index: number): void {
    this.value = index;
    this.dispatchEvent(
      new CustomEvent('luna-change', {
        detail: { value: index },
        bubbles: true,
        composed: true,
      })
    );
  }

  content() {
    return html`
      <div class="luna-control-choices">
        <span class="luna-control-choices__label">${this.label}</span>
        <div class="luna-control-choices__segmented" role="group" aria-label=${this.label}>
          ${this.choices.map(
            (choice, index) => html`
              <button
                type="button"
                class="luna-control-choices__option ${index === this.value ? 'luna-control-choices__option--selected' : ''}"
                aria-pressed=${index === this.value ? 'true' : 'false'}
                @click=${() => this.select(index)}
              >
                ${choice}
              </button>
            `
          )}
        </div>
        ${this.hint
          ? html`<span class="luna-control-choices__hint">${this.hint}</span>`
          : ''}
      </div>
    `;
  }

  styles() {
    return css`
      .luna-control-choices {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 420px;
        max-width: 100%;
      }

      .luna-control-choices__label {
        font-family: var(--mbw-font-sans);
        font-weight: 700;
        font-size: 15px; /* pinned: figma Luna Stepper (no Bold-15 type style) */
        line-height: 20px;
        color: var(--cds-text-primary);
      }

      .luna-control-choices__segmented {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .luna-control-choices__option {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        min-height: 44px;
        padding: 0 18px;
        border: none;
        border-radius: 10px; /* pinned: figma segmented choice (r10) */
        cursor: pointer;
        font-family: var(--mbw-font-sans);
        font-weight: 700;
        font-size: 15px; /* pinned: figma Luna Stepper (no Bold-15 type style) */
        line-height: 20px;
        color: var(--luna-outline-checkpoint-text);
        background: var(--luna-surface-field);
        box-shadow: inset 0 0 0 2px #cdd8ec; /* pinned: figma Luna Stepper */
      }

      .luna-control-choices__option--selected {
        background-image: linear-gradient(180deg, #ffffff 0%, #eef3fb 100%); /* pinned: figma Luna Stepper */
        box-shadow: inset 0 0 0 2px var(--luna-outline-checkpoint),
          0 3px 0 var(--luna-outline-checkpoint-ledge);
      }

      .luna-control-choices__option:focus-visible {
        outline: none;
        box-shadow:
          inset 0 0 0 2px var(--luna-outline-checkpoint),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .luna-control-choices__hint {
        font-family: var(--mbw-font-sans);
        font-weight: 400;
        font-size: 13px; /* pinned: figma Luna Stepper (no 13px type style) */
        line-height: 17px;
        color: var(--mbw-text-faint);
      }
    `;
  }
}
